import axios, { AxiosError } from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../useAuth";
import { APIError } from "@api/types/backendTypes";

export const useDeleteFacebookDataset = () => {
  const queryClient = useQueryClient();
  const { data } = useAuth();

  return useMutation({
    mutationFn: async ({
      id,
      organisationId,
    }: {
      id: number;
      organisationId: string;
    }) => {
      if (!data?.token) {
        throw new Error("User Session not available");
      }
      if (!id) {
        throw new Error("Dataset ID is required for deletion");
      }
      if (!organisationId) {
        throw new Error("Organisation ID is required for deletion");
      }

      try {
        const headers: Record<string, string> = {
          Accept: "application/json",
          "Content-Type": "application/json",
          "tracify-token": data?.token ?? "",
        };

        const response = await axios.delete(
          `${process.env.NEXT_PUBLIC_AD_CONNECTOR_V1_BASE_URL}/facebook-datasets/${organisationId}/${id}/delete`,
          {
            headers,
          }
        );

        if (response.status !== 204) {
          throw new Error(response.data.error);
        }

        return { success: true };
      } catch (err: any) {
        if (axios.isAxiosError(err)) {
          if (!err.response) {
            throw err;
          }
          const axiosError = err as AxiosError;

          // Handle specific error cases
          if (axiosError.response?.status === 404) {
            throw new Error("Dataset not found");
          } else if (axiosError.response?.status === 422) {
            throw new Error("Cannot delete dataset - invalid request");
          }

          const apiError = axiosError.response?.data as APIError;
          throw new Error(apiError?.error ?? axiosError.message);
        }
        throw new Error("Unknown error occurred!");
      }
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["datasets"] });
      queryClient.invalidateQueries({ queryKey: ["dataset", variables.id] });
    },
  });
};
